import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from 'sonner';
import { 
  MapPin, 
  Phone, 
  Mail, 
  ArrowRight, 
  Star, 
  Play,
  ChevronRight
} from 'lucide-react';

const DynamicPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    try {
      const res = await axios.get(`${API}/cms/pages/${slug}`);
      if (!res.data.published) {
        setError('Seite nicht gefunden');
        return;
      }
      setPage(res.data);
      
      // Set meta tags
      document.title = res.data.meta_title || res.data.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc && res.data.meta_description) {
        metaDesc.setAttribute('content', res.data.meta_description);
      }
    } catch (err) {
      setError('Seite nicht gefunden');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-slate-900 mb-4">Seite nicht gefunden</h1>
        <p className="text-slate-600 mb-6">Die angeforderte Seite existiert nicht.</p>
        <Button onClick={() => navigate('/')}>
          Zur Startseite
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid={`dynamic-page-${slug}`}>
      {page.blocks?.map((block, index) => (
        <BlockRenderer key={block.id || index} block={block} />
      ))}
    </div>
  );
};

// Block Renderer Component
const BlockRenderer = ({ block }) => {
  switch (block.type) {
    case 'hero':
      return <HeroBlockComponent block={block} />;
    case 'text':
      return <TextBlockComponent block={block} />;
    case 'image':
      return <ImageBlockComponent block={block} />;
    case 'image_text':
      return <ImageTextBlockComponent block={block} />;
    case 'cta':
      return <CTABlockComponent block={block} />;
    case 'features':
      return <FeaturesBlockComponent block={block} />;
    case 'faq':
      return <FAQBlockComponent block={block} />;
    case 'contact_form':
      return <ContactFormBlockComponent block={block} />;
    case 'testimonials':
      return <TestimonialsBlockComponent block={block} />;
    case 'locations':
      return <LocationsBlockComponent block={block} />;
    case 'video':
      return <VideoBlockComponent block={block} />;
    case 'spacer':
      return <SpacerBlockComponent block={block} />;
    case 'divider':
      return <DividerBlockComponent block={block} />;
    case 'gallery':
      return <GalleryBlockComponent block={block} />;
    default:
      return null;
  }
};

// Hero Block
const HeroBlockComponent = ({ block }) => {
  return (
    <section 
      className="relative min-h-[60vh] flex items-center justify-center bg-slate-900"
      style={{
        backgroundImage: block.background_image ? `url(${block.background_image})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div 
        className="absolute inset-0 bg-black"
        style={{ opacity: block.overlay_opacity || 0.5 }}
      ></div>
      <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
          {block.title}
        </h1>
        {block.subtitle && (
          <p className="text-lg md:text-xl text-white/90 mb-8">
            {block.subtitle}
          </p>
        )}
        {block.button_text && block.button_link && (
          <Link to={block.button_link}>
            <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8">
              {block.button_text}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        )}
      </div>
    </section>
  );
};

// Text Block
const TextBlockComponent = ({ block }) => {
  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };

  return (
    <section className="py-12 md:py-16">
      <div className="max-w-4xl mx-auto px-4">
        <div 
          className={`prose prose-lg max-w-none ${alignmentClasses[block.alignment || 'left']}`}
          dangerouslySetInnerHTML={{ __html: block.content }}
        />
      </div>
    </section>
  );
};

// Image Block
const ImageBlockComponent = ({ block }) => {
  const widthClasses = {
    small: 'max-w-md',
    medium: 'max-w-2xl',
    full: 'max-w-5xl'
  };

  return (
    <section className="py-8 md:py-12">
      <div className={`mx-auto px-4 ${widthClasses[block.width || 'full']}`}>
        <img 
          src={block.image_url} 
          alt={block.alt_text || ''} 
          className="w-full rounded-xl shadow-lg"
        />
        {block.caption && (
          <p className="text-center text-slate-500 mt-3 text-sm">{block.caption}</p>
        )}
      </div>
    </section>
  );
};

// Image + Text Block
const ImageTextBlockComponent = ({ block }) => {
  const isImageLeft = block.image_position !== 'right';

  return (
    <section className="py-12 md:py-20">
      <div className="max-w-6xl mx-auto px-4">
        <div className={`grid md:grid-cols-2 gap-8 md:gap-12 items-center ${!isImageLeft ? 'md:grid-flow-col-dense' : ''}`}>
          <div className={isImageLeft ? '' : 'md:col-start-2'}>
            <img 
              src={block.image_url} 
              alt={block.title || ''} 
              className="w-full rounded-xl shadow-lg"
            />
          </div>
          <div className={isImageLeft ? '' : 'md:col-start-1'}>
            {block.title && (
              <h2 className="text-3xl font-bold text-slate-900 mb-4">{block.title}</h2>
            )}
            <div 
              className="text-slate-600 prose"
              dangerouslySetInnerHTML={{ __html: block.content }}
            />
            {block.button_text && block.button_link && (
              <Link to={block.button_link} className="inline-block mt-6">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  {block.button_text}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// CTA Block
const CTABlockComponent = ({ block }) => {
  const bgColors = {
    blue: 'bg-blue-600',
    green: 'bg-green-600',
    orange: 'bg-orange-500',
    gray: 'bg-slate-800'
  };

  return (
    <section className={`py-16 md:py-20 ${bgColors[block.background_color || 'blue']}`}>
      <div className="max-w-4xl mx-auto px-4 text-center text-white">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">{block.title}</h2>
        {block.subtitle && (
          <p className="text-lg text-white/90 mb-8">{block.subtitle}</p>
        )}
        {block.button_text && block.button_link && (
          <form method="POST" target="_blank" action={block.button_link.startsWith('http') ? block.button_link : undefined}>
            {block.button_link.startsWith('http') ? (
              <Button type="submit" size="lg" variant="secondary" className="rounded-full px-8">
                {block.button_text}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Link to={block.button_link}>
                <Button size="lg" variant="secondary" className="rounded-full px-8">
                  {block.button_text}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            )}
          </form>
        )}
      </div>
    </section>
  );
};

// Features Block
const FeaturesBlockComponent = ({ block }) => {
  const colClasses = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4'
  };

  return (
    <section className="py-16 md:py-20 bg-slate-50">
      <div className="max-w-6xl mx-auto px-4">
        {block.title && (
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">{block.title}</h2>
        )}
        <div className={`grid gap-8 ${colClasses[block.columns || 3]}`}>
          {(block.features || []).map((feature, index) => (
            <Card key={index} className="bg-white">
              <CardContent className="p-6 text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">
                  {feature.icon || '✓'}
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Block
const FAQBlockComponent = ({ block }) => {
  return (
    <section className="py-16 md:py-20">
      <div className="max-w-3xl mx-auto px-4">
        {block.title && (
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">{block.title}</h2>
        )}
        <Accordion type="single" collapsible className="space-y-4">
          {(block.faqs || []).map((faq, index) => (
            <AccordionItem key={index} value={`faq-${index}`} className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Contact Form Block
const ContactFormBlockComponent = ({ block }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    message: ''
  });
  const [locations, setLocations] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (block.show_location_select) {
      axios.get(`${API}/locations`).then(res => setLocations(res.data));
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Nachricht gesendet!');
      setFormData({ name: '', email: '', phone: '', location: '', message: '' });
    } catch (err) {
      toast.error('Fehler beim Senden');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-16 md:py-20 bg-slate-50">
      <div className="max-w-xl mx-auto px-4">
        {block.title && (
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">{block.title}</h2>
        )}
        {block.subtitle && (
          <p className="text-slate-600 text-center mb-8">{block.subtitle}</p>
        )}
        <Card>
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  placeholder="Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Input
                  type="email"
                  placeholder="E-Mail *"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                />
              </div>
              {block.show_phone !== false && (
                <div>
                  <Input
                    placeholder="Telefon"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>
              )}
              {block.show_location_select !== false && locations.length > 0 && (
                <div>
                  <Select value={formData.location} onValueChange={(v) => setFormData({...formData, location: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Standort wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map(loc => (
                        <SelectItem key={loc.slug} value={loc.name}>{loc.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {block.show_message !== false && (
                <div>
                  <Textarea
                    placeholder="Nachricht"
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={4}
                  />
                </div>
              )}
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={submitting}>
                {submitting ? 'Senden...' : 'Nachricht senden'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

// Testimonials Block
const TestimonialsBlockComponent = ({ block }) => {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(res => {
      setTestimonials(res.data.slice(0, block.show_count || 3));
    });
  }, []);

  return (
    <section className="py-16 md:py-20 bg-slate-50">
      <div className="max-w-6xl mx-auto px-4">
        {block.title && (
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">{block.title}</h2>
        )}
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-600 mb-4">"{testimonial.text}"</p>
                <div className="font-medium text-slate-900">{testimonial.name}</div>
                <div className="text-sm text-slate-500">{testimonial.location}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Locations Block
const LocationsBlockComponent = ({ block }) => {
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    axios.get(`${API}/locations`).then(res => setLocations(res.data));
  }, []);

  return (
    <section className="py-16 md:py-20">
      <div className="max-w-6xl mx-auto px-4">
        {block.title && (
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">{block.title}</h2>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {locations.map((location, index) => (
            <Link key={index} to={`/standorte/${location.slug}`}>
              <Card className="h-full hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <MapPin className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-lg text-slate-900 mb-2">{location.name}</h3>
                  <p className="text-sm text-slate-600 mb-4">{location.address}</p>
                  <div className="flex items-center text-blue-600 text-sm font-medium">
                    Mehr erfahren
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Video Block
const VideoBlockComponent = ({ block }) => {
  const getEmbedUrl = (url) => {
    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    // Vimeo
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    return url;
  };

  return (
    <section className="py-12 md:py-16">
      <div className="max-w-4xl mx-auto px-4">
        {block.title && (
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-6">{block.title}</h2>
        )}
        <div className="relative aspect-video rounded-xl overflow-hidden shadow-lg">
          <iframe
            src={getEmbedUrl(block.video_url)}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>
    </section>
  );
};

// Spacer Block
const SpacerBlockComponent = ({ block }) => {
  const heights = {
    small: 'h-8',
    medium: 'h-16',
    large: 'h-32'
  };
  return <div className={heights[block.height || 'medium']} />;
};

// Divider Block
const DividerBlockComponent = ({ block }) => {
  if (block.style === 'dots') {
    return (
      <div className="py-8 text-center">
        <span className="text-2xl text-slate-300">• • •</span>
      </div>
    );
  }
  if (block.style === 'gradient') {
    return (
      <div className="py-8">
        <div className="max-w-xs mx-auto h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent rounded-full" />
      </div>
    );
  }
  return (
    <div className="py-8">
      <hr className="max-w-4xl mx-auto border-slate-200" />
    </div>
  );
};

// Gallery Block
const GalleryBlockComponent = ({ block }) => {
  const colClasses = {
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4'
  };

  return (
    <section className="py-12 md:py-16">
      <div className="max-w-6xl mx-auto px-4">
        {block.title && (
          <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">{block.title}</h2>
        )}
        <div className={`grid gap-4 ${colClasses[block.columns || 3]}`}>
          {(block.images || []).map((image, index) => (
            <div key={index} className="relative aspect-square rounded-xl overflow-hidden">
              <img 
                src={image.url} 
                alt={image.alt || ''} 
                className="w-full h-full object-cover"
              />
              {image.caption && (
                <div className="absolute inset-x-0 bottom-0 bg-black/50 text-white text-sm p-2">
                  {image.caption}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default DynamicPage;
